import * as React from 'react';
import { useEffect, useState } from 'react';
import { Col, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, generateLocalStateFromEventProperties, getSelectedAfr, updateVersionStateValue } from '../../redux/AfrSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { isAdmin, setIsDirty } from '../../redux/MiscSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import FactEditor from '../feature/FactEditor';
import SupplementalInformationForIndependent from '../feature/SupplementalInformationForIndependent';
import UploadDocumentByRole from '../feature/UploadDocumentByRole';
import Page from '../Page';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';

const AfrDataCore = (props: { userRole: string }) => {

    const dispatch = useAppDispatch();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const afrYear = currentAfr ? currentAfr.year : 0;
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    var ecode = entity ? entity.code : "";
    var ename = entity ? entity.name : "";
    const [activeTab, setActiveTab] = useState("1");
    const [state, setState] = useState(generateLocalStateFromEventProperties(currentAfr, ["reviewState_supplementalReviewed"]));

    const isAdminUser: boolean = useAppSelector(isAdmin);

    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const activeClass = (tab: string) => {
        return (tab === activeTab) ? "active" : "";
    }

    const showTab = function (tab: string) {
        var totalImpactFees = currentAfr != null ? utilityHelper.getImpactFeesTotal(currentAfr) : 0;
        switch (tab) {
            case "1":
                return (entity !== null)
            case "2":
                return (totalImpactFees !== 0);
            case "3":
                return (totalImpactFees !== 0);
            case "4":
                return (utilityHelper.isAffidavitRequired(currentAfr) && totalImpactFees !== 0);
            case "5":
                return (utilityHelper.showOtherSupplementalInformation(entity));
            case "6":
                return (utilityHelper.showOtherSupplementalInformation(entity));
        }
    }

    const handleChangeCheckbox = function (event: any) {

        var checkValues = JSON.parse(JSON.stringify(state));
        var key: string = event.target.name;
        var value: boolean = event.target.checked
        checkValues[key] = value;

        setState(checkValues);

        // here call to dispatcher sending:
        var eventFirstlevel = key.split("_")[0]
        var eventSecondlevel = key.split("_")[1]

        // Update global flag for this event in redux
        dispatch(updateVersionStateValue({ propGroup: eventFirstlevel, propName: eventSecondlevel, boolValue: value, value: null }));

        // set the dirty flag to TRUE
        dispatch(setIsDirty({ IsDirty: true, Page: "" }))

    }

    useEffect(() => {
        // get state from redux
        var newLocalState = generateLocalStateFromEventProperties(currentAfr, ["reviewState_supplementalReviewed"])

        // create a local state from redux
        setState(newLocalState)

    }, [currentAfr])


    useEffect(() => {

        // save all the pending changes into AFRSlice
        utilityHelper.saveDeferredChanges(dispatch);

    }, [activeTab]);

    return (
        <React.Fragment>
            <Page>
                <h2>Supplemental Information for {utilityHelper.getEntityName(entity, currentAfr)} ({utilityHelper.getEntityCode(entity, currentAfr)}), {afrYear}</h2>
                {isAdminUser &&
                    (<div className="box-events pl-2 pr-3 py-3 my-2">
                        <div>
                            <Label className="ml-4" check>
                                <Input type="checkbox"
                                    name="reviewState_supplementalReviewed"
                                    onChange={handleChangeCheckbox}
                                    checked={state["reviewState_supplementalReviewed"]}
                                    disabled={!utilityHelper.checkStatusList(currentAfr, enums.certifiedRecertified)}
                                /><b>Reviewed</b>
                            </Label>
                        </div>
                    </div>)}
                <Row>
                    <Col>
                        <div>
                            <h5>
                                <Nav tabs>
                                    {showTab("1") &&
                                        <NavItem>
                                            <NavLink
                                                className={activeClass('1')}
                                                onClick={() => { toggle('1'); }}
                                            >
                                                Population
                                            </NavLink>
                                        </NavItem>}
                                    <NavItem>
                                        <NavLink
                                            className={activeClass('2')}
                                            onClick={() => { toggle('2'); }}
                                        >
                                            Upload Exceptions
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeClass('3')}
                                            onClick={() => { toggle('3'); }}
                                        >
                                            Upload Waivers
                                        </NavLink>
                                    </NavItem>
                                    {showTab("4") &&
                                        <NavItem>
                                            <NavLink
                                                className={activeClass('4')}
                                                onClick={() => { toggle('4'); }}
                                            >
                                                Upload Affidavit
                                            </NavLink>
                                        </NavItem>}
                                    {showTab("5") &&
                                        <NavItem>
                                            <NavLink
                                                className={activeClass('5')}
                                                onClick={() => { toggle('5'); }}
                                            >
                                                Upload Variance
                                            </NavLink>
                                        </NavItem>}
                                    {showTab("6") &&
                                        <NavItem>
                                            <NavLink
                                                className={activeClass('6')}
                                                onClick={() => { toggle('6'); }}
                                            >
                                                Other
                                            </NavLink>
                                        </NavItem>}
                                </Nav>
                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        <Row>
                                            <Col sm="12">
                                                {showTab("1") &&
                                                    <React.Fragment>
                                                        <br />
                                                        <Row>
                                                            <Col md="1"></Col>
                                                            <Col className="text-right">
                                                                <Label >Population:</Label>
                                                            </Col>
                                                            <Col>
                                                                <FactEditor
                                                                    tableName="uas_001_unit-information"
                                                                    conceptId="Population"
                                                                    rowCode="population"
                                                                    columnCode="UnitInformationMember"
                                                                    allowDecimals={false}
                                                                    allowNegativeValue={false}
                                                                    prefix=""
                                                                    disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                                                                        || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                                                                    maxLength={10}
                                                                    placeholder="Please enter the population."
                                                                />

                                                            </Col>
                                                            <Col md="3"></Col>
                                                        </Row>
                                                    </React.Fragment>}
                                            </Col>
                                        </Row>
                                    </TabPane>
                                    <TabPane tabId="2">
                                        <Row>
                                            <Col sm="12">
                                                <UploadDocumentByRole role={enums.documentRole.impact_fee_exception} pdfType={enums.pdfType.AFRExceptions} />
                                            </Col>
                                        </Row>
                                    </TabPane>
                                    <TabPane tabId="3">
                                        <Row>
                                            <Col sm="12">
                                                <UploadDocumentByRole role={enums.documentRole.impact_fee_waiver} pdfType={enums.pdfType.AFRWaivers} />
                                            </Col>
                                        </Row>
                                    </TabPane>
                                    {showTab("4") &&
                                        <TabPane tabId="4">
                                            <Row>
                                                <Col sm="12">
                                                    <br />
                                                    <p>
                                                        Local Governments who report Impact Fees must upload an affidavit signed by its chief financial officer.
                                                    </p>
                                                    <p>
                                                        Please upload your signed affidavit required per 163.31801(8), F.S.
                                                    </p>
                                                    <UploadDocumentByRole role={enums.documentRole.affidavit} pdfType={enums.pdfType.AFRAffidavit} showCheckbox={false} />
                                                </Col>
                                            </Row>
                                        </TabPane>}
                                    {showTab("5") &&
                                        <TabPane tabId="5">
                                            <Row>
                                                <Col sm="12">
                                                    <br />
                                                    <p>
                                                        Per 218.32(1)(e)3., F.S., any district amending a final adopted budget must include a budget variance report. Would you like to upload a budget variance report?
                                                    </p>
                                                    <UploadDocumentByRole role={enums.documentRole.budget_variance_report} pdfType={enums.pdfType.AEBudgetVarianceReport} showCheckbox={true} />
                                                </Col>
                                            </Row>
                                        </TabPane>}
                                    {showTab("6") &&
                                        <TabPane tabId="6">
                                            <Row>
                                                <Col sm="12">
                                                    <SupplementalInformationForIndependent
                                                        entity={entity}
                                                    />
                                                </Col>
                                            </Row>
                                        </TabPane>}
                                </TabContent>
                            </h5>
                        </div>
                    </Col>
                </Row>
            </Page>
        </React.Fragment>
    );
}

export default AfrDataCore;